/**
 * The hourly strip's arithmetic (Phase 10 plan §4.3): the rain bar and the
 * time label. Plain TypeScript; `HourlyStrip.svelte` draws what these return.
 */

import type { HourlyPeriod } from '../api/types'
import { DAY, asZoneLocal, formatInZone } from '../time'

/** "Next 24 hours": everything the backend sends. */
export const HOURLY_CELLS = 24

/**
 * The rain bar's height as a fraction of its box: `pop / 100`, clamped.
 *
 * Never the mockup's `pop / 50`, which spills out of the box above 50%. A null
 * probability is null here too and draws no bar and no figure, which is a
 * different statement from 0%: an empty box and "0%".
 */
export function rainFraction(pop: number | null | undefined): number | null {
  if (pop == null || !Number.isFinite(pop)) return null
  return Math.min(1, Math.max(0, pop / 100))
}

export interface HourLabel {
  /** "7 PM", in the point's zone. */
  time: string
  /** "Tue" on the first cell of a new local day at the point; else null. */
  day: string | null
  /**
   * The day's first cell is exactly midnight, so the weekday alone says it.
   * "Tue 12 AM" at 11 px is wider than a 58 px cell and ran into the next
   * one (measured in the 10.1 captures); the time is kept for a screen
   * reader.
   */
  midnight: boolean
}

/**
 * The strip's time labels, in the point's zone: the same rule the meteogram's
 * axis follows, because an evening read against the viewer's clock is an
 * evening somewhere else. A cell that starts a new local day carries the
 * weekday. The first cell never does: it is "now", and the strip has no day
 * before it to have changed from.
 */
export function hourLabels(
  hours: readonly HourlyPeriod[],
  tz: string | null | undefined,
): HourLabel[] {
  let prev: number | null = null
  return hours.map((h) => {
    const t = h.start ? Date.parse(h.start) : NaN
    if (Number.isNaN(t)) {
      prev = null
      return { time: h.start ?? '', day: null, midnight: false }
    }
    const date = new Date(t)
    const localMs = asZoneLocal(date, tz)
    const local = Math.floor(localMs / DAY)
    const day = prev !== null && local !== prev ? formatInZone(date, tz, { weekday: 'short' }) : null
    prev = local
    return {
      time: formatInZone(date, tz, { hour: 'numeric' }),
      day,
      midnight: day !== null && localMs % DAY === 0,
    }
  })
}
